import { FC } from 'react'
import { useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import SaveIcon from '@mui/icons-material/Save'
import UndoIcon from '@mui/icons-material/Undo'
import { useNavigate } from 'react-router-dom'
import { aiTokens } from '../../admin/Workflow/aiGeneratorTokens'
import { themeColor } from '../../layout/theme/color'

interface WorkflowToolbarProps {
  workflowName: string
  onNameChange: (name: string) => void
  onAutoLayout: () => void
  onUndo: () => void
  onSave?: () => void
  onToggleCopilot?: () => void
  copilotOpen?: boolean
}

const WorkflowToolbar: FC<WorkflowToolbarProps> = ({
  workflowName,
  onNameChange,
  onAutoLayout,
  onUndo,
  onSave,
  onToggleCopilot,
  copilotOpen = false,
}) => {
  const translate = useTranslate()
  const navigate = useNavigate()

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        borderBottom: `1px solid ${themeColor.slate10}`,
        background: '#fff',
        minHeight: 52,
      }}
    >
      <Tooltip title={translate('ra.action.back')}>
        <IconButton size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 600,
          color: themeColor.slate,
          flex: 1,
          maxWidth: 400,
          px: 1,
        }}
        noWrap
      >
        {workflowName || translate('workflow.untitled')}
      </Typography>

      <Box sx={{ flex: 1 }} />

      <Tooltip title={translate('workflow.actions.undo')}>
        <IconButton size="small" onClick={onUndo}>
          <UndoIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title={translate('workflow.actions.autoLayout')}>
        <IconButton size="small" onClick={onAutoLayout}>
          <AccountTreeIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {onToggleCopilot && (
        <Tooltip title={translate('workflow.copilot.toggle')}>
          <IconButton
            size="small"
            onClick={onToggleCopilot}
            sx={{ color: copilotOpen ? aiTokens.accent : undefined }}
          >
            <AutoFixHighIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onSave && (
        <Button
          variant="contained"
          size="small"
          startIcon={<SaveIcon />}
          onClick={onSave}
          sx={{ textTransform: 'none', ml: 1 }}
        >
          {translate('ra.action.save')}
        </Button>
      )}
    </Box>
  )
}

export default WorkflowToolbar
